import React, { useEffect, useState } from 'react';

const API = 'https://api.ssgzone.in';

export default function SaasAdminDashboard() {
  const [section, setSection] = useState('dashboard');
  const [stats, setStats] = useState(null);
  const [tenants, setTenants] = useState([]);
  const [features, setFeatures] = useState([]);
  const [tenantPerms, setTenantPerms] = useState({});
  const [selectedTenant, setSelectedTenant] = useState('');
  const [apiKeys, setApiKeys] = useState(null);
  const [savingPerms, setSavingPerms] = useState(false);
  const [devTab, setDevTab] = useState('reference');
  const [webhookCfg, setWebhookCfg] = useState(null);
  const [webhookForm, setWebhookForm] = useState({ url: '', events: ['email.received','email.sent','user.created'], is_active: true });
  const [webhookSaving, setWebhookSaving] = useState(false);
  const [webhookTestResult, setWebhookTestResult] = useState(null);
  const [apiLogs, setApiLogs] = useState([]);
  const [logsLoading, setLogsLoading] = useState(false);
  const [branding, setBranding] = useState(null);
  const [brandingForm, setBrandingForm] = useState({ platform_name: '', tagline: '', primary_color: '#6366f1', secondary_color: '#8b5cf6', custom_domain: '', support_email: '' });
  const [brandingSaving, setBrandingSaving] = useState(false);
  const [tenantUsers, setTenantUsers] = useState([]);
  const [tenantUsersModal, setTenantUsersModal] = useState(null);
  const [editLimitsModal, setEditLimitsModal] = useState(null);
  const [limitsForm, setLimitsForm] = useState({ max_users: 50, plan_type: 'free' });
  const [analytics, setAnalytics] = useState(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  const [twoFAStatus, setTwoFAStatus] = useState(false);
  const [twoFASetup, setTwoFASetup] = useState(null);
  const [twoFACode, setTwoFACode] = useState('');
  const [twoFASaving, setTwoFASaving] = useState(false);
  const [showCreateTenant, setShowCreateTenant] = useState(false);
  const [newTenantForm, setNewTenantForm] = useState({ company_name: '', slug: '', admin_name: '', max_users: 50 });
  const [dnsModal, setDnsModal] = useState(null);
  const [quotaModal, setQuotaModal] = useState(null);
  const [quotaData, setQuotaData] = useState(null);
  const [quotaForm, setQuotaForm] = useState({ email_quota_mb: 1024, max_users: 50 });
  const [quotaSaving, setQuotaSaving] = useState(false);

  const user = JSON.parse(localStorage.getItem('user_data') || '{}');
  const token = localStorage.getItem('saas_admin_token');
  const auth = { Authorization: `Bearer ${token}` };

  const c = {
    primary: '#667eea', primaryLight: '#667eea22', success: '#10b981', successLight: '#d1fae5',
    warning: '#f59e0b', warningLight: '#fef3c7', danger: '#ef4444', dangerLight: '#fee2e2',
    text: '#1e293b', textMuted: '#64748b', border: '#e2e8f0', bg: '#f8fafc', card: '#ffffff'
  };

  useEffect(() => {
    if (!token) { window.location.href = '/'; return; }
    fetch(`${API}/api/saas-admin/dashboard/stats`, { headers: auth }).then(r => r.json()).then(d => d.success && setStats(d.data));
    fetch(`${API}/api/saas-admin/tenants`, { headers: auth }).then(r => r.json()).then(d => d.success && setTenants(d.data));
    fetch(`${API}/api/v1/permissions/features`).then(r => r.json()).then(d => d.success && setFeatures(d.data));
  }, []);

  useEffect(() => {
    if (!selectedTenant) return;
    fetch(`${API}/api/v1/permissions/tenant/${selectedTenant}`, { headers: auth })
      .then(r => r.json()).then(d => {
        if (d.success) {
          const map = {};
          d.data.forEach(f => { map[f.feature_key] = f.is_enabled; });
          setTenantPerms(map);
        }
      });
  }, [selectedTenant]);

  const loadApiKeys = () => {
    fetch(`${API}/api/saas-admin/api-keys`, { headers: auth }).then(r => r.json()).then(d => d.success && setApiKeys(d.data));
  };

  const loadWebhookCfg = () => {
    fetch(`${API}/api/saas-admin/webhook/config`, { headers: auth })
      .then(r => r.json()).then(d => {
        if (d.success && d.data) {
          setWebhookCfg(d.data);
          setWebhookForm({ url: d.data.url, events: d.data.events, is_active: d.data.is_active });
        }
      });
  };

  const saveWebhook = async () => {
    setWebhookSaving(true);
    const res = await fetch(`${API}/api/saas-admin/webhook/config`, {
      method: 'POST', headers: { ...auth, 'Content-Type': 'application/json' },
      body: JSON.stringify(webhookForm)
    });
    const data = await res.json();
    setWebhookSaving(false);
    if (data.success) { setWebhookCfg(data.data); alert('✅ Webhook saved!'); }
    else alert(data.error);
  };

  const testWebhook = async () => {
    setWebhookTestResult(null);
    const res = await fetch(`${API}/api/saas-admin/webhook/test`, { method: 'POST', headers: auth });
    const data = await res.json();
    setWebhookTestResult(data);
  };

  const loadApiLogs = async () => {
    setLogsLoading(true);
    const res = await fetch(`${API}/api/saas-admin/api-logs`, { headers: auth });
    const data = await res.json();
    if (data.success) setApiLogs(data.data);
    setLogsLoading(false);
  };

  const regenerateKeys = async () => {
    if (!window.confirm('Regenerate API keys? Your existing integrations will break.')) return;
    const res = await fetch(`${API}/api/saas-admin/keys/regenerate`, { method: 'POST', headers: auth });
    const data = await res.json();
    if (data.success) { setApiKeys(data.data); alert('✅ Keys regenerated!'); }
    else alert(data.error);
  };

  const loadBranding = () => {
    fetch(`${API}/api/saas-admin/branding`, { headers: auth })
      .then(r => r.json()).then(d => {
        if (d.success && d.data) {
          setBranding(d.data);
          setBrandingForm({ platform_name: d.data.platform_name || '', tagline: d.data.tagline || '', primary_color: d.data.primary_color || '#6366f1', secondary_color: d.data.secondary_color || '#8b5cf6', custom_domain: d.data.custom_domain || '', support_email: d.data.support_email || '' });
        }
      });
  };

  const saveBranding = async () => {
    setBrandingSaving(true);
    const res = await fetch(`${API}/api/saas-admin/branding`, {
      method: 'PUT', headers: { ...auth, 'Content-Type': 'application/json' },
      body: JSON.stringify(brandingForm)
    });
    const data = await res.json();
    setBrandingSaving(false);
    if (data.success) { setBranding(data.data); alert('✅ Branding saved!'); }
    else alert(data.error);
  };

  const savePerms = async () => {
    setSavingPerms(true);
    const res = await fetch(`${API}/api/v1/permissions/tenant/${selectedTenant}`, {
      method: 'PUT',
      headers: { ...auth, 'Content-Type': 'application/json' },
      body: JSON.stringify({ permissions: tenantPerms })
    });
    const data = await res.json();
    setSavingPerms(false);
    if (data.success) alert('✅ Tenant permissions saved!');
    else alert(data.error);
  };

  const loadQuota = async (tenant) => {
    setQuotaModal(tenant);
    setQuotaData(null);
    const res = await fetch(`${API}/api/saas-admin/tenants/${tenant.id}/quota`, { headers: auth });
    const data = await res.json();
    if (data.success) {
      setQuotaData(data.data);
      setQuotaForm({ email_quota_mb: data.data.email_quota_mb || 1024, max_users: data.data.max_users || 50 });
    }
  };

  const saveQuota = async () => {
    setQuotaSaving(true);
    const res = await fetch(`${API}/api/saas-admin/tenants/${quotaModal.id}/quota`, {
      method: 'PATCH', headers: { ...auth, 'Content-Type': 'application/json' },
      body: JSON.stringify(quotaForm)
    });
    const data = await res.json();
    setQuotaSaving(false);
    if (data.success) {
      setTenants(prev => prev.map(t => t.id === quotaModal.id ? { ...t, max_users: data.data.max_users } : t));
      setQuotaModal(null);
    } else alert(data.error);
  };

  const logout = () => { localStorage.clear(); window.location.href = '/'; };

  const toggleTenantStatus = async (tenant) => {
    const newStatus = tenant.status === 'active' ? 'suspended' : 'active';
    const res = await fetch(`${API}/api/saas-admin/tenants/${tenant.id}/status`, {
      method: 'PATCH', headers: { ...auth, 'Content-Type': 'application/json' },
      body: JSON.stringify({ status: newStatus })
    });
    const data = await res.json();
    if (data.success) setTenants(prev => prev.map(t => t.id === tenant.id ? { ...t, status: newStatus } : t));
    else alert(data.error);
  };

  const loadTenantUsers = async (tenant) => {
    const res = await fetch(`${API}/api/saas-admin/tenants/${tenant.id}/users`, { headers: auth });
    const data = await res.json();
    if (data.success) { setTenantUsers(data.data); setTenantUsersModal(tenant); }
    else alert(data.error);
  };

  const saveLimits = async () => {
    const res = await fetch(`${API}/api/saas-admin/tenants/${editLimitsModal.id}/limits`, {
      method: 'PATCH', headers: { ...auth, 'Content-Type': 'application/json' },
      body: JSON.stringify(limitsForm)
    });
    const data = await res.json();
    if (data.success) {
      setTenants(prev => prev.map(t => t.id === editLimitsModal.id ? { ...t, ...limitsForm } : t));
      setEditLimitsModal(null);
    } else alert(data.error);
  };

  const loadAnalytics = async () => {
    setAnalyticsLoading(true);
    const res = await fetch(`${API}/api/saas-admin/analytics`, { headers: auth });
    const data = await res.json();
    if (data.success) setAnalytics(data.data);
    setAnalyticsLoading(false);
  };

  const nav = [
    { id: 'dashboard', label: 'Dashboard', icon: '📊' },
    { id: 'tenants', label: 'Tenants', icon: '🏢' },
    { id: 'analytics', label: 'Analytics', icon: '📈' },
    { id: 'compose', label: 'Compose Email', icon: '✉️' },
    { id: 'permissions', label: 'Tenant Permissions', icon: '🔑' },
    { id: 'api-keys', label: 'API Keys', icon: '🗝' },
    { id: 'developer', label: 'Developer', icon: '⚡' },
    { id: 'branding', label: 'White-label', icon: '🎨' },
    { id: 'security', label: 'Security', icon: '🔒' },
    { id: 'billing', label: 'Billing', icon: '💳' },
  ];

  const Sidebar = () => (
    <div style={{ width: 220, minHeight: '100vh', background: c.card, borderRight: `1px solid ${c.border}`, display: 'flex', flexDirection: 'column', position: 'fixed', top: 0, left: 0, zIndex: 100 }}>
      <div style={{ padding: '20px 16px', borderBottom: `1px solid ${c.border}` }}>
        <div style={{ fontWeight: 700, fontSize: 15, color: c.text }}>{user.saas_name || 'SaaS Admin'}</div>
        <div style={{ fontSize: 11, color: c.textMuted, marginTop: 2 }}>{user.email}</div>
      </div>
      <div style={{ flex: 1, padding: '12px 8px' }}>
        {nav.map(item => (
          <div key={item.id} onClick={() => { setSection(item.id); if (item.id === 'api-keys') loadApiKeys(); if (item.id === 'developer') { loadApiKeys(); loadWebhookCfg(); } if (item.id === 'branding') loadBranding(); if (item.id === 'analytics') loadAnalytics(); if (item.id === 'security') fetch(API+'/api/saas-admin/2fa/status',{headers:auth}).then(r=>r.json()).then(d=>d.success&&setTwoFAStatus(d.data.enabled)); }}
            style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '9px 10px', borderRadius: 6, cursor: 'pointer', marginBottom: 2, background: section === item.id ? c.primaryLight : 'transparent', color: section === item.id ? c.primary : c.text, fontWeight: section === item.id ? 600 : 400, fontSize: 13 }}>
            <span>{item.icon}</span>{item.label}
          </div>
        ))}
      </div>
      <div style={{ padding: 16, borderTop: `1px solid ${c.border}` }}>
        <div onClick={logout} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 10px', borderRadius: 6, cursor: 'pointer', color: c.danger, fontSize: 13 }}>
          <span>⎋</span> Sign Out
        </div>
      </div>
    </div>
  );

  const Dashboard = () => (
    <div>
      <div style={{ fontSize: 22, fontWeight: 700, color: c.text, marginBottom: 4 }}>Welcome, {user.name || user.email}</div>
      <div style={{ fontSize: 13, color: c.textMuted, marginBottom: 24 }}>SaaS: {user.saas_name} · {user.saas_slug}</div>
      {stats && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16, marginBottom: 24 }}>
          {[['Total Tenants', stats.total_tenants, '🏢'], ['Active Tenants', stats.active_tenants, '✅'], ['Total Users', stats.total_users, '👥'], ['Active Users', stats.active_users, '🟢']].map(([label, val, icon]) => (
            <div key={label} style={{ background: c.card, border: `1px solid ${c.border}`, borderRadius: 12, padding: 20, textAlign: 'center' }}>
              <div style={{ fontSize: 28, marginBottom: 4 }}>{icon}</div>
              <div style={{ fontSize: 26, fontWeight: 700, color: c.text }}>{val || 0}</div>
              <div style={{ fontSize: 12, color: c.textMuted }}>{label}</div>
            </div>
          ))}
        </div>
      )}
      <div style={{ background: c.card, border: `1px solid ${c.border}`, borderRadius: 12, padding: 20 }}>
        <div style={{ fontWeight: 700, fontSize: 15, color: c.text, marginBottom: 12 }}>Your Enabled Features</div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
          {Object.entries(user.permissions || {}).map(([key, val]) => (
            <span key={key} style={{ background: val ? c.successLight : c.dangerLight, color: val ? c.success : c.danger, borderRadius: 20, padding: '4px 12px', fontSize: 12, fontWeight: 600 }}>
              {val ? '✓' : '✗'} {key}
            </span>
          ))}
        </div>
      </div>
    </div>
  );

  const TenantsView = () => (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
          <div style={{ fontSize: 22, fontWeight: 700, color: c.text }}>Tenants</div>
          <button onClick={() => setShowCreateTenant(p => !p)} style={{ background: 'linear-gradient(135deg,#6366f1,#8b5cf6)', color: '#fff', border: 'none', borderRadius: 8, padding: '9px 18px', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>+ New Tenant</button>
        </div>
        {showCreateTenant && (
          <div style={{ background: c.card, border: `1px solid ${c.border}`, borderRadius: 12, padding: 20, marginBottom: 20 }}>
            <div style={{ fontSize: 14, fontWeight: 700, color: c.text, marginBottom: 14 }}>Create New Tenant</div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 10 }}>
              <input value={newTenantForm.company_name} onChange={e => setNewTenantForm(p=>({...p,company_name:e.target.value}))} placeholder="Company Name *" style={{ padding:'9px 12px', border:`1px solid ${c.border}`, borderRadius:7, fontSize:13, outline:'none' }} />
              <input value={newTenantForm.slug} onChange={e => setNewTenantForm(p=>({...p,slug:e.target.value}))} placeholder="Slug (e.g. acme) *" style={{ padding:'9px 12px', border:`1px solid ${c.border}`, borderRadius:7, fontSize:13, outline:'none' }} />
              <input value={newTenantForm.admin_name} onChange={e => setNewTenantForm(p=>({...p,admin_name:e.target.value}))} placeholder="Admin Name *" style={{ padding:'9px 12px', border:`1px solid ${c.border}`, borderRadius:7, fontSize:13, outline:'none' }} />
              <input type="number" value={newTenantForm.max_users} onChange={e => setNewTenantForm(p=>({...p,max_users:parseInt(e.target.value)||50}))} placeholder="Max Users" style={{ padding:'9px 12px', border:`1px solid ${c.border}`, borderRadius:7, fontSize:13, outline:'none' }} />
            </div>
            <div style={{ display:'flex', gap:10 }}>
              <button onClick={createTenant} style={{ background:'linear-gradient(135deg,#6366f1,#8b5cf6)', color:'#fff', border:'none', borderRadius:7, padding:'9px 20px', fontSize:13, fontWeight:600, cursor:'pointer' }}>Create</button>
              <button onClick={() => setShowCreateTenant(false)} style={{ background:'none', border:`1px solid ${c.border}`, borderRadius:7, padding:'9px 16px', fontSize:13, cursor:'pointer', color:c.textMuted }}>Cancel</button>
            </div>
          </div>
        )}
      <div style={{ background: c.card, border: `1px solid ${c.border}`, borderRadius: 12, overflow: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead><tr style={{ background: c.bg, borderBottom: `1px solid ${c.border}` }}>
            {['Company', 'Domain', 'Plan', 'Users', 'Status', 'Actions'].map(h => <th key={h} style={{ textAlign: 'left', padding: '12px 16px', color: c.textMuted, fontWeight: 600, fontSize: 12 }}>{h}</th>)}
          </tr></thead>
          <tbody>
            {tenants.map(t => (
              <tr key={t.id} style={{ borderBottom: `1px solid ${c.border}` }}>
                <td style={{ padding: '12px 16px', fontWeight: 600, color: c.text }}>{t.company_name}</td>
                <td style={{ padding: '12px 16px', color: c.textMuted, fontSize: 12, fontFamily: 'monospace' }}>{t.domain}</td>
                <td style={{ padding: '12px 16px', color: c.text }}>{t.plan_type || 'free'}</td>
                <td style={{ padding: '12px 16px', color: c.text }}>{t.user_count || 0} / {t.max_users}</td>
                <td style={{ padding: '12px 16px' }}>
                  <span style={{ background: t.status === 'active' ? c.successLight : c.warningLight, color: t.status === 'active' ? c.success : c.warning, borderRadius: 20, padding: '3px 10px', fontSize: 11, fontWeight: 600 }}>{t.status}</span>
                </td>
                <td style={{ padding: '12px 16px' }}>
                  <div style={{ display: 'flex', gap: 6 }}>
                    <button onClick={() => loadQuota(t)}
                      style={{ background: 'none', border: `1px solid ${c.border}`, borderRadius: 6, padding: '4px 10px', fontSize: 11, cursor: 'pointer', color: c.text }}>⚙ Quota</button>
                    <button onClick={() => loadTenantUsers(t)}
                      style={{ background: 'none', border: `1px solid ${c.border}`, borderRadius: 6, padding: '4px 10px', fontSize: 11, cursor: 'pointer', color: c.text }}>👥 Users</button>
                    <button onClick={() => { setEditLimitsModal(t); setLimitsForm({ max_users: t.max_users, plan_type: t.plan_type || 'free' }); }}
                      style={{ background: 'none', border: `1px solid ${c.border}`, borderRadius: 6, padding: '4px 10px', fontSize: 11, cursor: 'pointer', color: c.text }}>✏️ Limits</button>
                    <button onClick={() => toggleTenantStatus(t)}
                      style={{ background: 'none', border: `1px solid ${t.status === 'active' ? c.danger : c.success}`, borderRadius: 6, padding: '4px 10px', fontSize: 11, cursor: 'pointer', color: t.status === 'active' ? c.danger : c.success, fontWeight: 600 }}>
                      {t.status === 'active' ? 'Suspend' : 'Activate'}
                    </button>
                  </div>
                </td>
              </tr>
            ))}
            {!tenants.length && <tr><td colSpan={6} style={{ padding: 30, textAlign: 'center', color: c.textMuted }}>No tenants yet</td></tr>}
          </tbody>
        </table>
      </div>

      {quotaModal && (
        <div style={{ position: 'fixed', inset: 0, background: '#0008', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: c.card, borderRadius: 14, padding: 28, width: 420, boxShadow: '0 20px 60px #0003' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
              <div style={{ fontSize: 17, fontWeight: 700, color: c.text }}>⚙ Quota — {quotaModal.company_name}</div>
              <button onClick={() => setQuotaModal(null)} style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: c.textMuted }}>✕</button>
            </div>
            {!quotaData && <div style={{ color: c.textMuted, fontSize: 13 }}>Loading...</div>}
            {quotaData && (
              <>
                <div style={{ marginBottom: 16 }}>
                  <div style={{ fontSize: 12, fontWeight: 600, color: c.textMuted, marginBottom: 4 }}>Storage Used</div>
                  <div style={{ background: c.border, borderRadius: 20, height: 10, overflow: 'hidden', marginBottom: 4 }}>
                    <div style={{ width: `${Math.min(100, Math.round((quotaData.storage_used_mb / (quotaData.email_quota_mb || 1024)) * 100))}%`, height: '100%', background: c.primary, borderRadius: 20 }} />
                  </div>
                  <div style={{ fontSize: 12, color: c.textMuted }}>{quotaData.storage_used_mb} MB / {quotaData.email_quota_mb || 1024} MB</div>
                </div>
                <div style={{ marginBottom: 20 }}>
                  <div style={{ fontSize: 12, fontWeight: 600, color: c.textMuted, marginBottom: 4 }}>Users</div>
                  <div style={{ background: c.border, borderRadius: 20, height: 10, overflow: 'hidden', marginBottom: 4 }}>
                    <div style={{ width: `${Math.min(100, Math.round((quotaData.current_users / (quotaData.max_users || 1)) * 100))}%`, height: '100%', background: c.success, borderRadius: 20 }} />
                  </div>
                  <div style={{ fontSize: 12, color: c.textMuted }}>{quotaData.current_users} / {quotaData.max_users} users</div>
                </div>
                <label style={{ fontSize: 12, fontWeight: 600, color: c.textMuted, display: 'block', marginBottom: 4 }}>Email Quota (MB)</label>
                <input type="number" value={quotaForm.email_quota_mb} onChange={e => setQuotaForm(p => ({ ...p, email_quota_mb: parseInt(e.target.value) || 1024 }))}
                  style={{ width: '100%', padding: '10px 12px', border: `1px solid ${c.border}`, borderRadius: 8, fontSize: 13, outline: 'none', boxSizing: 'border-box', marginBottom: 14 }} />
                <label style={{ fontSize: 12, fontWeight: 600, color: c.textMuted, display: 'block', marginBottom: 4 }}>Max Users</label>
                <input type="number" value={quotaForm.max_users} onChange={e => setQuotaForm(p => ({ ...p, max_users: parseInt(e.target.value) || 50 }))}
                  style={{ width: '100%', padding: '10px 12px', border: `1px solid ${c.border}`, borderRadius: 8, fontSize: 13, outline: 'none', boxSizing: 'border-box', marginBottom: 20 }} />
                <div style={{ display: 'flex', gap: 10 }}>
                  <button onClick={saveQuota} disabled={quotaSaving}
                    style={{ flex: 1, background: c.primary, color: '#fff', border: 'none', borderRadius: 8, padding: '10px', fontSize: 13, fontWeight: 600, cursor: 'pointer', opacity: quotaSaving ? 0.7 : 1 }}>
                    {quotaSaving ? 'Saving...' : 'Save Quota'}
                  </button>
                  <button onClick={() => setQuotaModal(null)}
                    style={{ flex: 1, background: 'none', border: `1px solid ${c.border}`, borderRadius: 8, padding: '10px', fontSize: 13, cursor: 'pointer', color: c.text }}>Cancel</button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {tenantUsersModal && (
        <div style={{ position: 'fixed', inset: 0, background: '#0008', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: c.card, borderRadius: 14, padding: 28, width: 700, maxHeight: '80vh', overflow: 'auto', boxShadow: '0 20px 60px #0003' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
              <div style={{ fontSize: 17, fontWeight: 700, color: c.text }}>Users — {tenantUsersModal.company_name}</div>
              <button onClick={() => setTenantUsersModal(null)} style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: c.textMuted }}>✕</button>
            </div>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead><tr style={{ background: c.bg, borderBottom: `1px solid ${c.border}` }}>
                {['Email','Name','Role','Status','Last Login'].map(h => <th key={h} style={{ textAlign: 'left', padding: '10px 12px', color: c.textMuted, fontWeight: 600, fontSize: 11 }}>{h}</th>)}
              </tr></thead>
              <tbody>
                {tenantUsers.map(u => (
                  <tr key={u.id} style={{ borderBottom: `1px solid ${c.border}` }}>
                    <td style={{ padding: '10px 12px', fontFamily: 'monospace', fontSize: 12 }}>{u.email}</td>
                    <td style={{ padding: '10px 12px' }}>{[u.first_name, u.last_name].filter(Boolean).join(' ') || u.username}</td>
                    <td style={{ padding: '10px 12px', color: c.textMuted }}>{u.role}</td>
                    <td style={{ padding: '10px 12px' }}>
                      <span style={{ background: u.status === 'active' ? c.successLight : c.warningLight, color: u.status === 'active' ? c.success : c.warning, borderRadius: 20, padding: '2px 8px', fontSize: 11, fontWeight: 600 }}>{u.status}</span>
                    </td>
                    <td style={{ padding: '10px 12px', color: c.textMuted, fontSize: 12 }}>{u.last_login ? new Date(u.last_login).toLocaleDateString() : '—'}</td>
                  </tr>
                ))}
                {!tenantUsers.length && <tr><td colSpan={5} style={{ padding: 20, textAlign: 'center', color: c.textMuted }}>No users</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {editLimitsModal && (
        <div style={{ position: 'fixed', inset: 0, background: '#0008', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: c.card, borderRadius: 14, padding: 28, width: 380, boxShadow: '0 20px 60px #0003' }}>
            <div style={{ fontSize: 17, fontWeight: 700, color: c.text, marginBottom: 20 }}>Edit Limits — {editLimitsModal.company_name}</div>
            <label style={{ fontSize: 12, fontWeight: 600, color: c.textMuted, display: 'block', marginBottom: 4 }}>Max Users</label>
            <input type="number" value={limitsForm.max_users} onChange={e => setLimitsForm(p => ({ ...p, max_users: parseInt(e.target.value) }))}
              style={{ width: '100%', padding: '10px 12px', border: `1px solid ${c.border}`, borderRadius: 8, fontSize: 13, outline: 'none', boxSizing: 'border-box', marginBottom: 14 }} />
            <label style={{ fontSize: 12, fontWeight: 600, color: c.textMuted, display: 'block', marginBottom: 4 }}>Plan Type</label>
            <select value={limitsForm.plan_type} onChange={e => setLimitsForm(p => ({ ...p, plan_type: e.target.value }))}
              style={{ width: '100%', padding: '10px 12px', border: `1px solid ${c.border}`, borderRadius: 8, fontSize: 13, outline: 'none', boxSizing: 'border-box', marginBottom: 20 }}>
              {['free','starter','pro','enterprise'].map(p => <option key={p} value={p}>{p}</option>)}
            </select>
            <div style={{ display: 'flex', gap: 10 }}>
              <button onClick={saveLimits}
                style={{ flex: 1, background: c.primary, color: '#fff', border: 'none', borderRadius: 8, padding: '10px', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>Save</button>
              <button onClick={() => setEditLimitsModal(null)}
                style={{ flex: 1, background: 'none', border: `1px solid ${c.border}`, borderRadius: 8, padding: '10px', fontSize: 13, cursor: 'pointer', color: c.text }}>Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  const PermissionsView = () => {
    const categories = [...new Set(features.map(f => f.category))];
    return (
      <div>
        <div style={{ fontSize: 22, fontWeight: 700, color: c.text, marginBottom: 4 }}>Tenant Permissions</div>
        <div style={{ fontSize: 13, color: c.textMuted, marginBottom: 20 }}>Assign feature access to your tenants (cannot exceed your own SaaS permissions)</div>
        <div style={{ display: 'flex', gap: 12, marginBottom: 24, alignItems: 'center' }}>
          <select value={selectedTenant} onChange={e => setSelectedTenant(e.target.value)}
            style={{ padding: '10px 14px', border: `1px solid ${c.border}`, borderRadius: 8, fontSize: 13, color: c.text, background: c.card, outline: 'none', minWidth: 240 }}>
            <option value="">— Select Tenant —</option>
            {tenants.map(t => <option key={t.id} value={t.id}>{t.company_name}</option>)}
          </select>
          {selectedTenant && (
            <button onClick={savePerms} disabled={savingPerms}
              style={{ background: c.primary, color: '#fff', border: 'none', borderRadius: 8, padding: '10px 20px', fontSize: 13, fontWeight: 600, cursor: 'pointer', opacity: savingPerms ? 0.7 : 1 }}>
              {savingPerms ? 'Saving...' : 'Save'}
            </button>
          )}
        </div>
        {selectedTenant && categories.map(cat => (
          <div key={cat} style={{ background: c.card, border: `1px solid ${c.border}`, borderRadius: 12, padding: 20, marginBottom: 16 }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: c.textMuted, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 12 }}>{cat}</div>
            {features.filter(f => f.category === cat).map(f => {
              const saasAllows = user.permissions?.[f.feature_key] !== false;
              return (
                <div key={f.feature_key} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 0', borderBottom: `1px solid ${c.border}`, opacity: saasAllows ? 1 : 0.4 }}>
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 600, color: c.text }}>{f.feature_name}</div>
                    {!saasAllows && <div style={{ fontSize: 11, color: c.danger }}>Not available in your SaaS plan</div>}
                  </div>
                  <div onClick={() => saasAllows && setTenantPerms(p => ({ ...p, [f.feature_key]: !p[f.feature_key] }))}
                    style={{ width: 44, height: 24, borderRadius: 12, background: (saasAllows && tenantPerms[f.feature_key]) ? c.success : c.border, cursor: saasAllows ? 'pointer' : 'not-allowed', position: 'relative', transition: 'background 0.2s', flexShrink: 0 }}>
                    <div style={{ width: 18, height: 18, borderRadius: '50%', background: '#fff', position: 'absolute', top: 3, left: (saasAllows && tenantPerms[f.feature_key]) ? 23 : 3, transition: 'left 0.2s' }} />
                  </div>
                </div>
              );
            })}
          </div>
        ))}
        {!selectedTenant && <div style={{ textAlign: 'center', padding: 60, color: c.textMuted }}><div style={{ fontSize: 40, marginBottom: 12 }}>🔑</div><div style={{ fontSize: 15, fontWeight: 600, color: c.text }}>Select a Tenant</div></div>}
      </div>
    );
  };

  const ApiKeysView = () => (
    <div>
      <div style={{ fontSize: 22, fontWeight: 700, color: c.text, marginBottom: 4 }}>API Keys</div>
      <div style={{ fontSize: 13, color: c.textMuted, marginBottom: 20 }}>Use these credentials to integrate SSGzone Mail into your application</div>
      {apiKeys ? (
        <div style={{ background: c.card, border: `1px solid ${c.border}`, borderRadius: 12, padding: 24, maxWidth: 600 }}>
          <div style={{ background: c.warningLight, borderRadius: 8, padding: 12, marginBottom: 20, fontSize: 12, color: c.warning }}>⚠ Keep these credentials secure. Never expose in client-side code.</div>
          {[['API Key', apiKeys.api_key], ['API Secret', apiKeys.api_secret], ['Webhook Secret', apiKeys.webhook_secret]].map(([label, val]) => val && (
            <div key={label} style={{ marginBottom: 16 }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: c.textMuted, marginBottom: 6 }}>{label}</div>
              <div style={{ display: 'flex', gap: 8 }}>
                <div style={{ flex: 1, padding: '10px 12px', background: c.bg, border: `1px solid ${c.border}`, borderRadius: 8, fontFamily: 'monospace', fontSize: 12, color: c.text, wordBreak: 'break-all' }}>{val}</div>
                <button onClick={() => { navigator.clipboard.writeText(val); alert('Copied!'); }}
                  style={{ background: c.bg, border: `1px solid ${c.border}`, borderRadius: 8, padding: '10px 14px', cursor: 'pointer', fontSize: 13 }}>📋</button>
              </div>
            </div>
          ))}
          <div style={{ marginTop: 20, padding: 16, background: c.bg, borderRadius: 8, border: `1px solid ${c.border}` }}>
            <div style={{ fontSize: 12, fontWeight: 600, color: c.text, marginBottom: 8 }}>SSO Integration Example</div>
            <pre style={{ fontSize: 11, fontFamily: 'monospace', color: c.textMuted, margin: 0, whiteSpace: 'pre-wrap' }}>{`POST https://api.ssgzone.in/api/saas-admin/sso/generate\nX-Api-Key: ${apiKeys.api_key || '<your-api-key>'}\n{\n  "user_email": "employee@yourapp.com",\n  "tenant_slug": "your-company"\n}`}</pre>
          </div>
          <div style={{ marginTop: 16, paddingTop: 16, borderTop: `1px solid ${c.border}` }}>
            <button onClick={regenerateKeys}
              style={{ background: 'none', border: `1px solid ${c.danger}`, borderRadius: 8, padding: '9px 18px', fontSize: 13, color: c.danger, cursor: 'pointer', fontWeight: 600 }}>
              🔄 Regenerate API Keys
            </button>
            <div style={{ fontSize: 11, color: c.textMuted, marginTop: 6 }}>Warning: this will invalidate your current keys immediately.</div>
          </div>
        </div>
      ) : (
        <div style={{ textAlign: 'center', padding: 40, color: c.textMuted }}>Loading...</div>
      )}
    </div>
  );

  const ALL_EVENTS = ['email.received','email.sent','user.created','user.deleted','tenant.created','tenant.suspended'];

  const DeveloperView = () => (
    <div>
      <div style={{ fontSize: 22, fontWeight: 700, color: c.text, marginBottom: 4 }}>Developer Portal</div>
      <div style={{ fontSize: 13, color: c.textMuted, marginBottom: 20 }}>Integrate SSGzone Mail into your application</div>
      <div style={{ display: 'flex', gap: 4, marginBottom: 24, borderBottom: `1px solid ${c.border}` }}>
        {[['reference','📖 API Reference'],['webhooks','🔔 Webhooks'],['logs','📋 API Logs']].map(([id, label]) => (
          <button key={id} onClick={() => { setDevTab(id); if (id === 'logs') loadApiLogs(); }}
            style={{ padding: '9px 18px', border: 'none', borderBottom: devTab === id ? `2px solid ${c.primary}` : '2px solid transparent', background: 'none', cursor: 'pointer', fontSize: 13, fontWeight: devTab === id ? 700 : 400, color: devTab === id ? c.primary : c.textMuted, marginBottom: -1 }}>
            {label}
          </button>
        ))}
      </div>

      {devTab === 'reference' && (
        <div>
          <div style={{ background: c.card, border: `1px solid ${c.border}`, borderRadius: 12, overflow: 'hidden', marginBottom: 20 }}>
            <div style={{ padding: '14px 20px', borderBottom: `1px solid ${c.border}`, background: c.bg }}>
              <div style={{ fontSize: 14, fontWeight: 700, color: c.text }}>Base URL</div>
              <div style={{ fontFamily: 'monospace', fontSize: 13, color: c.primary, marginTop: 4 }}>https://api.ssgzone.in/api/v1/webmail</div>
            </div>
            <div style={{ padding: '14px 20px', background: c.bg }}>
              <div style={{ fontSize: 14, fontWeight: 700, color: c.text }}>Authentication</div>
              <div style={{ fontFamily: 'monospace', fontSize: 12, color: c.textMuted, marginTop: 4 }}>Authorization: Bearer &lt;jwt_token&gt;</div>
            </div>
          </div>
          {[
            { method: 'POST', path: '/auth/login', desc: 'Authenticate a tenant user', body: '{ "email": "user@tenant.ssgzone.in", "password": "..." }' },
            { method: 'GET', path: '/inbox?folder=inbox&page=1&limit=25', desc: 'List emails in a folder', body: null },
            { method: 'GET', path: '/email/:id', desc: 'Get full email content', body: null },
            { method: 'POST', path: '/send', desc: 'Send an email', body: '{ "to": "...", "subject": "...", "html_content": "..." }' },
            { method: 'PATCH', path: '/email/:id/star', desc: 'Toggle star on email', body: null },
            { method: 'PATCH', path: '/email/:id/move', desc: 'Move email to folder', body: '{ "folder": "trash" }' },
            { method: 'DELETE', path: '/email/:id', desc: 'Delete / move to trash', body: null },
            { method: 'GET', path: '/folders/counts', desc: 'Get unread counts per folder', body: null },
            { method: 'GET', path: '/analytics', desc: 'Email analytics for user', body: null },
            { method: 'GET', path: '/templates', desc: 'List email templates', body: null },
            { method: 'POST', path: '/templates', desc: 'Create email template', body: '{ "name": "...", "subject": "...", "html_body": "..." }' },
            { method: 'POST', path: '/auth/sso', desc: 'SSO token exchange', body: '{ "sso_token": "..." }' },
          ].map((ep, i) => (
            <div key={i} style={{ background: c.card, border: `1px solid ${c.border}`, borderRadius: 10, padding: '14px 18px', marginBottom: 10, display: 'flex', gap: 16, alignItems: 'flex-start' }}>
              <span style={{ background: ep.method === 'GET' ? '#dbeafe' : ep.method === 'POST' ? '#d1fae5' : ep.method === 'DELETE' ? '#fee2e2' : '#fef3c7', color: ep.method === 'GET' ? '#1d4ed8' : ep.method === 'POST' ? '#065f46' : ep.method === 'DELETE' ? '#991b1b' : '#92400e', borderRadius: 6, padding: '3px 10px', fontSize: 11, fontWeight: 700, fontFamily: 'monospace', flexShrink: 0, marginTop: 2 }}>{ep.method}</span>
              <div style={{ flex: 1 }}>
                <div style={{ fontFamily: 'monospace', fontSize: 13, color: c.text, marginBottom: 2 }}>{ep.path}</div>
                <div style={{ fontSize: 12, color: c.textMuted }}>{ep.desc}</div>
                {ep.body && <div style={{ fontFamily: 'monospace', fontSize: 11, color: c.textMuted, background: c.bg, borderRadius: 6, padding: '6px 10px', marginTop: 6 }}>{ep.body}</div>}
              </div>
            </div>
          ))}
          <div style={{ background: c.card, border: `1px solid ${c.border}`, borderRadius: 12, padding: 20, marginTop: 8 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: c.text, marginBottom: 10 }}>SSO Integration (Server-side)</div>
            <pre style={{ fontSize: 11, fontFamily: 'monospace', color: c.textMuted, margin: 0, whiteSpace: 'pre-wrap', background: c.bg, padding: 14, borderRadius: 8 }}>{`# 1. Generate SSO token from your backend\nPOST https://api.ssgzone.in/api/saas-admin/sso/generate\nX-Api-Key: ${apiKeys?.api_key || '<your-api-key>'}\n{ "user_email": "user@yourapp.com", "tenant_slug": "your-company" }\n\n# 2. Redirect user to webmail\nhttps://mail.ssgzone.in/?sso_token=<token>`}</pre>
          </div>
        </div>
      )}

      {devTab === 'webhooks' && (
        <div style={{ maxWidth: 600 }}>
          <div style={{ background: c.card, border: `1px solid ${c.border}`, borderRadius: 12, padding: 24, marginBottom: 16 }}>
            <div style={{ fontSize: 14, fontWeight: 700, color: c.text, marginBottom: 16 }}>Webhook Endpoint</div>
            <input value={webhookForm.url} onChange={e => setWebhookForm(p => ({ ...p, url: e.target.value }))}
              placeholder="https://yourapp.com/webhooks/ssgzone"
              style={{ width: '100%', padding: '10px 12px', border: `1px solid ${c.border}`, borderRadius: 8, fontSize: 13, outline: 'none', boxSizing: 'border-box', marginBottom: 16 }} />
            <div style={{ fontSize: 13, fontWeight: 600, color: c.text, marginBottom: 10 }}>Events to receive</div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 16 }}>
              {ALL_EVENTS.map(ev => {
                const checked = webhookForm.events.includes(ev);
                return (
                  <div key={ev} onClick={() => setWebhookForm(p => ({ ...p, events: checked ? p.events.filter(e => e !== ev) : [...p.events, ev] }))}
                    style={{ padding: '5px 12px', borderRadius: 20, border: `1px solid ${checked ? c.primary : c.border}`, background: checked ? c.primaryLight : 'transparent', color: checked ? c.primary : c.textMuted, fontSize: 12, cursor: 'pointer', fontWeight: checked ? 600 : 400 }}>
                    {ev}
                  </div>
                );
              })}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
              <div onClick={() => setWebhookForm(p => ({ ...p, is_active: !p.is_active }))}
                style={{ width: 44, height: 24, borderRadius: 12, background: webhookForm.is_active ? c.success : c.border, cursor: 'pointer', position: 'relative', transition: 'background 0.2s', flexShrink: 0 }}>
                <div style={{ width: 18, height: 18, borderRadius: '50%', background: '#fff', position: 'absolute', top: 3, left: webhookForm.is_active ? 23 : 3, transition: 'left 0.2s' }} />
              </div>
              <span style={{ fontSize: 13, color: c.text }}>Webhook active</span>
            </div>
            <div style={{ display: 'flex', gap: 10 }}>
              <button onClick={saveWebhook} disabled={webhookSaving}
                style={{ background: c.primary, color: '#fff', border: 'none', borderRadius: 8, padding: '10px 20px', fontSize: 13, fontWeight: 600, cursor: 'pointer', opacity: webhookSaving ? 0.7 : 1 }}>
                {webhookSaving ? 'Saving...' : '💾 Save Webhook'}
              </button>
              {webhookCfg && (
                <button onClick={testWebhook}
                  style={{ background: 'none', border: `1px solid ${c.border}`, borderRadius: 8, padding: '10px 16px', fontSize: 13, cursor: 'pointer', color: c.text }}>
                  🔔 Send Test Ping
                </button>
              )}
            </div>
            {webhookTestResult && (
              <div style={{ marginTop: 14, padding: 12, borderRadius: 8, background: webhookTestResult.success ? c.successLight : c.dangerLight, color: webhookTestResult.success ? c.success : c.danger, fontSize: 13 }}>
                {webhookTestResult.success
                  ? `✅ Ping delivered — HTTP ${webhookTestResult.data.status_code} in ${webhookTestResult.data.duration_ms}ms`
                  : `❌ Ping failed — ${webhookTestResult.error}`}
              </div>
            )}
          </div>
          {webhookCfg?.secret && (
            <div style={{ background: c.card, border: `1px solid ${c.border}`, borderRadius: 12, padding: 20 }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: c.text, marginBottom: 8 }}>Webhook Signature Verification</div>
              <div style={{ fontSize: 12, color: c.textMuted, marginBottom: 10 }}>Verify incoming webhooks using HMAC-SHA256 with your secret:</div>
              <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
                <div style={{ flex: 1, padding: '10px 12px', background: c.bg, border: `1px solid ${c.border}`, borderRadius: 8, fontFamily: 'monospace', fontSize: 12, color: c.text, wordBreak: 'break-all' }}>{webhookCfg.secret}</div>
                <button onClick={() => { navigator.clipboard.writeText(webhookCfg.secret); alert('Copied!'); }}
                  style={{ background: c.bg, border: `1px solid ${c.border}`, borderRadius: 8, padding: '10px 14px', cursor: 'pointer', fontSize: 13 }}>📋</button>
              </div>
              <pre style={{ fontSize: 11, fontFamily: 'monospace', color: c.textMuted, background: c.bg, padding: 12, borderRadius: 8, margin: 0, whiteSpace: 'pre-wrap' }}>{`// Node.js verification\nconst sig = req.headers['x-ssgzone-signature'];\nconst expected = crypto.createHmac('sha256', WEBHOOK_SECRET)\n  .update(JSON.stringify(req.body)).digest('hex');\nif (sig !== expected) return res.status(401).send('Invalid signature');`}</pre>
            </div>
          )}
        </div>
      )}

      {devTab === 'logs' && (
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
            <div style={{ fontSize: 13, color: c.textMuted }}>Last 50 API requests for your SaaS</div>
            <button onClick={loadApiLogs} style={{ background: 'none', border: `1px solid ${c.border}`, borderRadius: 7, padding: '7px 14px', fontSize: 12, cursor: 'pointer', color: c.text }}>↻ Refresh</button>
          </div>
          {logsLoading && <div style={{ textAlign: 'center', padding: 40, color: c.textMuted }}>Loading...</div>}
          {!logsLoading && apiLogs.length === 0 && (
            <div style={{ textAlign: 'center', padding: 60, color: c.textMuted }}>
              <div style={{ fontSize: 36, marginBottom: 10 }}>📋</div>
              <div style={{ fontSize: 14, fontWeight: 600, color: c.text, marginBottom: 6 }}>No API logs yet</div>
              <div style={{ fontSize: 13 }}>Logs will appear here as your integration makes API calls</div>
            </div>
          )}
          {apiLogs.length > 0 && (
            <div style={{ background: c.card, border: `1px solid ${c.border}`, borderRadius: 12, overflow: 'hidden' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                <thead><tr style={{ background: c.bg, borderBottom: `1px solid ${c.border}` }}>
                  {['Method','Endpoint','Status','Duration','Time'].map(h => (
                    <th key={h} style={{ textAlign: 'left', padding: '10px 14px', color: c.textMuted, fontWeight: 600, fontSize: 11 }}>{h}</th>
                  ))}
                </tr></thead>
                <tbody>
                  {apiLogs.map((log, i) => (
                    <tr key={i} style={{ borderBottom: `1px solid ${c.border}` }}>
                      <td style={{ padding: '10px 14px' }}>
                        <span style={{ background: log.method === 'GET' ? '#dbeafe' : log.method === 'POST' ? '#d1fae5' : '#fee2e2', color: log.method === 'GET' ? '#1d4ed8' : log.method === 'POST' ? '#065f46' : '#991b1b', borderRadius: 4, padding: '2px 7px', fontSize: 10, fontWeight: 700, fontFamily: 'monospace' }}>{log.method}</span>
                      </td>
                      <td style={{ padding: '10px 14px', fontFamily: 'monospace', color: c.text }}>{log.endpoint}</td>
                      <td style={{ padding: '10px 14px' }}>
                        <span style={{ color: log.status_code < 300 ? c.success : log.status_code < 500 ? c.warning : c.danger, fontWeight: 600 }}>{log.status_code}</span>
                      </td>
                      <td style={{ padding: '10px 14px', color: c.textMuted }}>{log.duration_ms}ms</td>
                      <td style={{ padding: '10px 14px', color: c.textMuted }}>{new Date(log.created_at).toLocaleTimeString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );

  const BrandingView = () => {
    const inputS = { width: '100%', padding: '10px 12px', border: `1px solid ${c.border}`, borderRadius: 8, fontSize: 13, color: c.text, background: c.bg, outline: 'none', boxSizing: 'border-box', marginBottom: 12 };
    const labelS = { fontSize: 12, fontWeight: 600, color: c.textMuted, marginBottom: 4, display: 'block' };
    return (
      <div>
        <div style={{ fontSize: 22, fontWeight: 700, color: c.text, marginBottom: 4 }}>White-label Branding</div>
        <div style={{ fontSize: 13, color: c.textMuted, marginBottom: 24 }}>Customize how SSGzone Mail appears to your tenants</div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, maxWidth: 800 }}>
          <div style={{ background: c.card, border: `1px solid ${c.border}`, borderRadius: 12, padding: 24 }}>
            <div style={{ fontSize: 14, fontWeight: 700, color: c.text, marginBottom: 16 }}>Identity</div>
            <label style={labelS}>Platform Name</label>
            <input style={inputS} value={brandingForm.platform_name} onChange={e => setBrandingForm(p => ({ ...p, platform_name: e.target.value }))} placeholder="My Mail Platform" />
            <label style={labelS}>Tagline</label>
            <input style={inputS} value={brandingForm.tagline} onChange={e => setBrandingForm(p => ({ ...p, tagline: e.target.value }))} placeholder="Powered by your brand" />
            <label style={labelS}>Support Email</label>
            <input style={inputS} value={brandingForm.support_email} onChange={e => setBrandingForm(p => ({ ...p, support_email: e.target.value }))} placeholder="support@yourapp.com" />
            <label style={labelS}>Custom Domain (webmail)</label>
            <input style={inputS} value={brandingForm.custom_domain} onChange={e => setBrandingForm(p => ({ ...p, custom_domain: e.target.value }))} placeholder="mail.yourapp.com" />
          </div>
          <div style={{ background: c.card, border: `1px solid ${c.border}`, borderRadius: 12, padding: 24 }}>
            <div style={{ fontSize: 14, fontWeight: 700, color: c.text, marginBottom: 16 }}>Colors</div>
            <label style={labelS}>Primary Color</label>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 12 }}>
              <input type="color" value={brandingForm.primary_color} onChange={e => setBrandingForm(p => ({ ...p, primary_color: e.target.value }))} style={{ width: 40, height: 40, border: 'none', borderRadius: 8, cursor: 'pointer' }} />
              <input style={{ ...inputS, marginBottom: 0, flex: 1 }} value={brandingForm.primary_color} onChange={e => setBrandingForm(p => ({ ...p, primary_color: e.target.value }))} />
            </div>
            <label style={labelS}>Secondary Color</label>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 20 }}>
              <input type="color" value={brandingForm.secondary_color} onChange={e => setBrandingForm(p => ({ ...p, secondary_color: e.target.value }))} style={{ width: 40, height: 40, border: 'none', borderRadius: 8, cursor: 'pointer' }} />
              <input style={{ ...inputS, marginBottom: 0, flex: 1 }} value={brandingForm.secondary_color} onChange={e => setBrandingForm(p => ({ ...p, secondary_color: e.target.value }))} />
            </div>
            <div style={{ padding: 16, borderRadius: 10, background: brandingForm.primary_color + '22', border: `1px solid ${brandingForm.primary_color}44`, marginBottom: 16 }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: brandingForm.primary_color }}>{brandingForm.platform_name || 'Your Platform'}</div>
              <div style={{ fontSize: 11, color: brandingForm.secondary_color }}>{brandingForm.tagline || 'Your tagline here'}</div>
            </div>
            <button onClick={saveBranding} disabled={brandingSaving}
              style={{ background: c.primary, color: '#fff', border: 'none', borderRadius: 8, padding: '10px 20px', fontSize: 13, fontWeight: 600, cursor: 'pointer', opacity: brandingSaving ? 0.7 : 1, width: '100%' }}>
              {brandingSaving ? 'Saving...' : '💾 Save Branding'}
            </button>
          </div>
        </div>
        {branding && (
          <div style={{ marginTop: 20, background: c.card, border: `1px solid ${c.border}`, borderRadius: 12, padding: 20, maxWidth: 800 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: c.text, marginBottom: 12 }}>Logo Upload</div>
            <div style={{ fontSize: 12, color: c.textMuted, marginBottom: 12 }}>Upload logo as base64 — paste data URL or use the API directly</div>
            <div style={{ display: 'flex', gap: 16, alignItems: 'center' }}>
              {branding.logo_url && <img src={branding.logo_url} alt="logo" style={{ width: 60, height: 60, borderRadius: 8, objectFit: 'contain', border: `1px solid ${c.border}` }} />}
              <label style={{ background: c.primary, color: '#fff', borderRadius: 8, padding: '8px 16px', fontSize: 12, cursor: 'pointer', fontWeight: 600 }}>
                Upload Logo
                <input type="file" accept="image/*" style={{ display: 'none' }} onChange={async e => {
                  const file = e.target.files[0]; if (!file) return;
                  const reader = new FileReader();
                  reader.onload = async ev => {
                    const res = await fetch(`${API}/api/saas-admin/branding/logo`, {
                      method: 'POST', headers: { ...auth, 'Content-Type': 'application/json' },
                      body: JSON.stringify({ logo_base64: ev.target.result, type: 'logo' })
                    });
                    const data = await res.json();
                    if (data.success) { loadBranding(); alert('✅ Logo uploaded!'); } else alert(data.error);
                  };
                  reader.readAsDataURL(file);
                }} />
              </label>
            </div>
          </div>
        )}
      </div>
    );
  };

  const AnalyticsView = () => {
    if (analyticsLoading) return <div style={{ textAlign: 'center', padding: 60, color: c.textMuted }}>Loading...</div>;
    if (!analytics) return null;
    const { totals, tenants: rows } = analytics;
    return (
      <div>
        <div style={{ fontSize: 22, fontWeight: 700, color: c.text, marginBottom: 20 }}>Analytics</div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16, marginBottom: 24 }}>
          {[['Total Tenants', totals.total_tenants, '🏢'], ['Active Tenants', totals.active_tenants, '✅'], ['Total Users', totals.total_users, '👥'], ['Total Emails', totals.total_emails, '📧']].map(([label, val, icon]) => (
            <div key={label} style={{ background: c.card, border: `1px solid ${c.border}`, borderRadius: 12, padding: 20, textAlign: 'center' }}>
              <div style={{ fontSize: 28, marginBottom: 4 }}>{icon}</div>
              <div style={{ fontSize: 26, fontWeight: 700, color: c.text }}>{val || 0}</div>
              <div style={{ fontSize: 12, color: c.textMuted }}>{label}</div>
            </div>
          ))}
        </div>
        <div style={{ background: c.card, border: `1px solid ${c.border}`, borderRadius: 12, overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead><tr style={{ background: c.bg, borderBottom: `1px solid ${c.border}` }}>
              {['Company','Domain','Plan','Users','Emails','Status'].map(h => <th key={h} style={{ textAlign: 'left', padding: '12px 16px', color: c.textMuted, fontWeight: 600, fontSize: 12 }}>{h}</th>)}
            </tr></thead>
            <tbody>
              {rows.map(t => (
                <tr key={t.id} style={{ borderBottom: `1px solid ${c.border}` }}>
                  <td style={{ padding: '12px 16px', fontWeight: 600, color: c.text }}>{t.company_name}</td>
                  <td style={{ padding: '12px 16px', color: c.textMuted, fontSize: 12, fontFamily: 'monospace' }}>{t.domain}</td>
                  <td style={{ padding: '12px 16px', color: c.text }}>{t.plan_type || 'free'}</td>
                  <td style={{ padding: '12px 16px', color: c.text }}>{t.user_count} / {t.max_users}</td>
                  <td style={{ padding: '12px 16px', color: c.text }}>{t.email_count}</td>
                  <td style={{ padding: '12px 16px' }}>
                    <span style={{ background: t.status === 'active' ? c.successLight : c.warningLight, color: t.status === 'active' ? c.success : c.warning, borderRadius: 20, padding: '3px 10px', fontSize: 11, fontWeight: 600 }}>{t.status}</span>
                  </td>
                </tr>
              ))}
              {!rows.length && <tr><td colSpan={6} style={{ padding: 30, textAlign: 'center', color: c.textMuted }}>No data</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    );
  };


  const createTenant = async () => {
    const { company_name, slug, admin_name, max_users } = newTenantForm;
    if (!company_name || !slug || !admin_name) return alert('company_name, slug, admin_name required');
    try {
      const res = await fetch(API+'/api/saas-admin/tenants', {
        method: 'POST', headers: { ...auth, 'Content-Type': 'application/json' },
        body: JSON.stringify({ company_name, slug, admin_name, max_users })
      });
      const data = await res.json();
      if (data.success) {
        setTenants(prev => [data.data, ...prev]);
        setShowCreateTenant(false);
        setNewTenantForm({ company_name: '', slug: '', admin_name: '', max_users: 50 });
        setDnsModal({ domain: data.data.domain, password: data.data.admin_credentials?.password, id: data.data.id });
      } else alert(data.error);
    } catch (err) { alert(err.message); }
  };

  const SecurityView = () => {
    const [localCode, setLocalCode] = React.useState('');
    const setup2FA = async () => {
      setTwoFASaving(true);
      const res = await fetch(API+'/api/saas-admin/2fa/setup', { method: 'POST', headers: auth });
      const d = await res.json();
      if (d.success) setTwoFASetup(d.data);
      setTwoFASaving(false);
    };
    const enable2FA = async () => {
      setTwoFASaving(true);
      const res = await fetch(API+'/api/saas-admin/2fa/enable', {
        method: 'POST', headers: { ...auth, 'Content-Type': 'application/json' },
        body: JSON.stringify({ token: localCode })
      });
      const d = await res.json();
      if (d.success) { setTwoFAStatus(true); setTwoFASetup(null); setLocalCode(''); alert('2FA enabled!'); }
      else alert(d.error);
      setTwoFASaving(false);
    };
    const disable2FA = async () => {
      if (!window.confirm('Disable 2FA?')) return;
      const res = await fetch(API+'/api/saas-admin/2fa/disable', { method: 'POST', headers: auth });
      const d = await res.json();
      if (d.success) { setTwoFAStatus(false); alert('2FA disabled'); }
      else alert(d.error);
    };
    return (
      <div style={{ maxWidth: 520 }}>
        <div style={{ fontSize: 22, fontWeight: 700, color: c.text, marginBottom: 20 }}>Security — Two-Factor Authentication</div>
        <div style={{ background: c.card, border: `1px solid ${c.border}`, borderRadius: 12, padding: 24, marginBottom: 20 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
            <span style={{ fontSize: 28 }}>{twoFAStatus ? '🔒' : '🔓'}</span>
            <div>
              <div style={{ fontSize: 15, fontWeight: 700, color: c.text }}>2FA is {twoFAStatus ? 'Enabled' : 'Disabled'}</div>
              <div style={{ fontSize: 12, color: c.textMuted }}>{twoFAStatus ? 'Your account is protected with TOTP' : 'Enable 2FA for extra security'}</div>
            </div>
          </div>
          {!twoFAStatus && !twoFASetup && (
            <button onClick={setup2FA} disabled={twoFASaving}
              style={{ background: 'linear-gradient(135deg,#6366f1,#8b5cf6)', color: '#fff', border: 'none', borderRadius: 8, padding: '9px 20px', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
              {twoFASaving ? 'Loading...' : 'Setup 2FA'}
            </button>
          )}
          {twoFASetup && (
            <div>
              <div style={{ fontSize: 13, color: c.textMuted, marginBottom: 12 }}>Scan this QR code with Google Authenticator or Authy:</div>
              <img src={twoFASetup.qr_code} alt="QR" style={{ width: 180, height: 180, border: `1px solid ${c.border}`, borderRadius: 8, marginBottom: 12 }} />
              <div style={{ fontSize: 11, color: c.textMuted, marginBottom: 12, fontFamily: 'monospace', background: c.bg, padding: '6px 10px', borderRadius: 6 }}>Secret: {twoFASetup.secret}</div>
              <input value={localCode} onChange={e => setLocalCode(e.target.value)} maxLength={6}
                placeholder="Enter 6-digit code"
                style={{ width: '100%', padding: '9px 12px', border: `1px solid ${c.border}`, borderRadius: 7, fontSize: 16, textAlign: 'center', letterSpacing: 6, outline: 'none', boxSizing: 'border-box', marginBottom: 10 }} />
              <button onClick={enable2FA} disabled={twoFASaving || localCode.length !== 6}
                style={{ background: '#10b981', color: '#fff', border: 'none', borderRadius: 8, padding: '9px 20px', fontSize: 13, fontWeight: 600, cursor: 'pointer', opacity: localCode.length !== 6 ? 0.6 : 1 }}>
                {twoFASaving ? 'Verifying...' : 'Enable 2FA'}
              </button>
            </div>
          )}
          {twoFAStatus && (
            <button onClick={disable2FA}
              style={{ background: '#ef4444', color: '#fff', border: 'none', borderRadius: 8, padding: '9px 20px', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
              Disable 2FA
            </button>
          )}
        </div>
      </div>
    );
  };
  const BillingView = () => {
    const BAPI = 'https://api.ssgzone.in/api/v1/billing';
    const [billingTab, setBillingTab] = React.useState('tenants');
    const [plans, setPlans] = React.useState([]);
    const [tenantBilling, setTenantBilling] = React.useState([]);
    const [assignModal, setAssignModal] = React.useState(null); // tenant row
    const [invoicesModal, setInvoicesModal] = React.useState(null); // tenant row
    const [invoices, setInvoices] = React.useState([]);
    const [newInvoiceForm, setNewInvoiceForm] = React.useState({ amount: '', billing_period_start: '', billing_period_end: '', status: 'pending', notes: '' });
    const [showNewInvoice, setShowNewInvoice] = React.useState(false);
    const [assignForm, setAssignForm] = React.useState({ plan_id: '', billing_cycle: 'monthly', custom_price: '', currency: 'INR', status: 'active', next_billing_date: '', trial_ends_at: '', notes: '' });
    const [saving, setSaving] = React.useState(false);

    const fetchPlans = async () => {
      const res = await fetch(`${BAPI}/saas-admin/plans`, { headers: auth });
      const data = await res.json();
      if (data.success) setPlans(data.data);
    };

    const fetchTenantBilling = async () => {
      const res = await fetch(`${BAPI}/saas-admin/tenants`, { headers: auth });
      const data = await res.json();
      if (data.success) setTenantBilling(data.data);
    };

    React.useEffect(() => { fetchPlans(); fetchTenantBilling(); }, []);

    const openAssign = (row) => {
      setAssignModal(row);
      setAssignForm({
        plan_id: row.plan_id || '',
        billing_cycle: row.billing_cycle || 'monthly',
        custom_price: row.custom_price || '',
        currency: row.currency || 'INR',
        status: row.billing_status || 'active',
        next_billing_date: row.next_billing_date ? row.next_billing_date.slice(0, 10) : '',
        trial_ends_at: row.trial_ends_at ? row.trial_ends_at.slice(0, 10) : '',
        notes: row.notes || ''
      });
    };

    const saveAssign = async () => {
      setSaving(true);
      try {
        const res = await fetch(`${BAPI}/saas-admin/tenants/${assignModal.id}/assign`, {
          method: 'POST', headers: { ...auth, 'Content-Type': 'application/json' },
          body: JSON.stringify({ ...assignForm, custom_price: assignForm.custom_price ? parseFloat(assignForm.custom_price) : null })
        });
        const data = await res.json();
        if (data.success) { setAssignModal(null); fetchTenantBilling(); }
        else alert(data.error);
      } catch (err) { alert(err.message); }
      setSaving(false);
    };

    const removeBilling = async (tenantId) => {
      if (!window.confirm('Remove billing assignment for this tenant?')) return;
      const res = await fetch(`${BAPI}/saas-admin/tenants/${tenantId}/billing`, { method: 'DELETE', headers: auth });
      const data = await res.json();
      if (data.success) fetchTenantBilling();
      else alert(data.error);
    };

    const openInvoices = async (row) => {
      setInvoicesModal(row);
      setShowNewInvoice(false);
      setNewInvoiceForm({ amount: '', billing_period_start: '', billing_period_end: '', status: 'pending', notes: '' });
      const res = await fetch(`${BAPI}/saas-admin/invoices/${row.id}`, { headers: auth });
      const data = await res.json();
      if (data.success) setInvoices(data.data);
    };

    const createInvoice = async () => {
      if (!newInvoiceForm.amount || !newInvoiceForm.billing_period_start || !newInvoiceForm.billing_period_end) return alert('Amount and billing period required');
      const res = await fetch(`${BAPI}/saas-admin/invoices`, {
        method: 'POST', headers: { ...auth, 'Content-Type': 'application/json' },
        body: JSON.stringify({ tenant_id: invoicesModal.id, plan_id: invoicesModal.plan_id || null, ...newInvoiceForm, amount: parseFloat(newInvoiceForm.amount) })
      });
      const data = await res.json();
      if (data.success) { setInvoices(prev => [data.data, ...prev]); setShowNewInvoice(false); }
      else alert(data.error);
    };

    const markInvoice = async (invoiceId, status) => {
      const res = await fetch(`${BAPI}/saas-admin/invoices/${invoiceId}/status`, {
        method: 'PATCH', headers: { ...auth, 'Content-Type': 'application/json' },
        body: JSON.stringify({ status })
      });
      const data = await res.json();
      if (data.success) setInvoices(prev => prev.map(i => i.id === invoiceId ? data.data : i));
      else alert(data.error);
    };

    const statusColors = { active: { bg: c.successLight, color: c.success }, trial: { bg: '#cffafe', color: '#0891b2' }, past_due: { bg: c.dangerLight, color: c.danger }, cancelled: { bg: '#f3f4f6', color: '#9ca3af' }, suspended: { bg: c.warningLight, color: c.warning } };
    const invoiceStatusColors = { pending: { bg: c.warningLight, color: c.warning }, paid: { bg: c.successLight, color: c.success }, failed: { bg: c.dangerLight, color: c.danger }, refunded: { bg: '#f3f4f6', color: '#9ca3af' }, waived: { bg: '#ede9fe', color: '#7c3aed' } };
    const inputS = { width: '100%', padding: '10px 12px', border: `1px solid ${c.border}`, borderRadius: 8, fontSize: 13, color: c.text, background: c.bg, outline: 'none', boxSizing: 'border-box', marginBottom: 12 };
    const labelS = { fontSize: 12, fontWeight: 600, color: c.textMuted, marginBottom: 4, display: 'block' };

    return (
      <div>
        <div style={{ fontSize: 22, fontWeight: 700, color: c.text, marginBottom: 4 }}>Billing</div>
        <div style={{ fontSize: 13, color: c.textMuted, marginBottom: 20 }}>Manage billing plans and tenant subscriptions</div>

        <div style={{ display: 'flex', gap: 4, marginBottom: 20, borderBottom: `1px solid ${c.border}` }}>
          {[['tenants', '🏢 Tenant Billing'], ['plans', '💳 Available Plans']].map(([id, label]) => (
            <button key={id} onClick={() => setBillingTab(id)}
              style={{ padding: '9px 18px', border: 'none', borderBottom: billingTab === id ? `2px solid ${c.primary}` : '2px solid transparent', background: 'none', cursor: 'pointer', fontSize: 13, fontWeight: billingTab === id ? 700 : 400, color: billingTab === id ? c.primary : c.textMuted, marginBottom: -1 }}>
              {label}
            </button>
          ))}
        </div>

        {billingTab === 'tenants' && (
          <div style={{ background: c.card, border: `1px solid ${c.border}`, borderRadius: 12, overflow: 'hidden' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead><tr style={{ background: c.bg, borderBottom: `1px solid ${c.border}` }}>
                {['Tenant', 'Plan', 'Price', 'Cycle', 'Status', 'Next Billing', 'Actions'].map(h => (
                  <th key={h} style={{ textAlign: 'left', padding: '12px 16px', color: c.textMuted, fontWeight: 600, fontSize: 12 }}>{h}</th>
                ))}
              </tr></thead>
              <tbody>
                {tenantBilling.map(row => (
                  <tr key={row.id} style={{ borderBottom: `1px solid ${c.border}` }}>
                    <td style={{ padding: '12px 16px', fontWeight: 600, color: c.text }}>{row.company_name}</td>
                    <td style={{ padding: '12px 16px', color: c.text }}>{row.plan_name || <span style={{ color: c.textMuted }}>Not assigned</span>}</td>
                    <td style={{ padding: '12px 16px', color: c.text }}>
                      {row.custom_price
                        ? <span>{row.currency} {Number(row.custom_price).toLocaleString()} <span style={{ fontSize: 10, color: c.warning }}>(custom)</span></span>
                        : row.price_monthly ? `${row.plan_currency || 'INR'} ${Number(row.price_monthly).toLocaleString()}` : '—'}
                    </td>
                    <td style={{ padding: '12px 16px', color: c.textMuted, fontSize: 12 }}>{row.billing_cycle || '—'}</td>
                    <td style={{ padding: '12px 16px' }}>
                      {row.billing_status
                        ? <span style={{ background: (statusColors[row.billing_status] || {}).bg || '#f3f4f6', color: (statusColors[row.billing_status] || {}).color || '#9ca3af', borderRadius: 20, padding: '3px 10px', fontSize: 11, fontWeight: 600 }}>{row.billing_status}</span>
                        : <span style={{ color: c.textMuted, fontSize: 12 }}>Unassigned</span>}
                    </td>
                    <td style={{ padding: '12px 16px', color: c.textMuted, fontSize: 12 }}>{row.next_billing_date ? new Date(row.next_billing_date).toLocaleDateString() : '—'}</td>
                    <td style={{ padding: '12px 16px' }}>
                      <div style={{ display: 'flex', gap: 6 }}>
                        <button onClick={() => openAssign(row)} style={{ background: 'none', border: `1px solid ${c.border}`, borderRadius: 6, padding: '4px 10px', fontSize: 11, cursor: 'pointer', color: c.text }}>✏️ Assign</button>
                        <button onClick={() => openInvoices(row)} style={{ background: 'none', border: `1px solid ${c.border}`, borderRadius: 6, padding: '4px 10px', fontSize: 11, cursor: 'pointer', color: c.text }}>📄 Invoices</button>
                        {row.billing_id && <button onClick={() => removeBilling(row.id)} style={{ background: 'none', border: `1px solid ${c.danger}`, borderRadius: 6, padding: '4px 10px', fontSize: 11, cursor: 'pointer', color: c.danger }}>✕ Remove</button>}
                      </div>
                    </td>
                  </tr>
                ))}
                {!tenantBilling.length && <tr><td colSpan={7} style={{ padding: 30, textAlign: 'center', color: c.textMuted }}>No tenants found</td></tr>}
              </tbody>
            </table>
          </div>
        )}

        {billingTab === 'plans' && (
          <div style={{ background: c.card, border: `1px solid ${c.border}`, borderRadius: 12, overflow: 'hidden' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead><tr style={{ background: c.bg, borderBottom: `1px solid ${c.border}` }}>
                {['Plan', 'Monthly', 'Yearly', 'Max Users', 'Storage', 'Emails/Month', 'Tenants'].map(h => (
                  <th key={h} style={{ textAlign: 'left', padding: '12px 16px', color: c.textMuted, fontWeight: 600, fontSize: 12 }}>{h}</th>
                ))}
              </tr></thead>
              <tbody>
                {plans.map(p => (
                  <tr key={p.id} style={{ borderBottom: `1px solid ${c.border}` }}>
                    <td style={{ padding: '12px 16px', fontWeight: 600, color: c.text }}>{p.name}<div style={{ fontSize: 11, color: c.textMuted, fontFamily: 'monospace' }}>{p.slug}</div></td>
                    <td style={{ padding: '12px 16px', color: c.text, fontWeight: 600 }}>{p.currency} {Number(p.price_monthly).toLocaleString()}</td>
                    <td style={{ padding: '12px 16px', color: c.textMuted }}>{p.currency} {Number(p.price_yearly).toLocaleString()}</td>
                    <td style={{ padding: '12px 16px', color: c.text }}>{p.max_users}</td>
                    <td style={{ padding: '12px 16px', color: c.text }}>{p.max_storage_gb} GB</td>
                    <td style={{ padding: '12px 16px', color: c.text }}>{Number(p.max_emails_per_month).toLocaleString()}</td>
                    <td style={{ padding: '12px 16px', color: c.text }}>{p.tenant_count || 0}</td>
                  </tr>
                ))}
                {!plans.length && <tr><td colSpan={7} style={{ padding: 30, textAlign: 'center', color: c.textMuted }}>No plans available. Ask your Super Admin to create billing plans.</td></tr>}
              </tbody>
            </table>
          </div>
        )}

        {/* Assign Plan Modal */}
        {assignModal && (
          <div style={{ position: 'fixed', inset: 0, background: '#0008', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }} onClick={() => setAssignModal(null)}>
            <div style={{ background: c.card, borderRadius: 14, padding: 28, width: 500, maxHeight: '90vh', overflowY: 'auto' }} onClick={e => e.stopPropagation()}>
              <div style={{ fontSize: 17, fontWeight: 700, color: c.text, marginBottom: 4 }}>Assign Billing — {assignModal.company_name}</div>
              <div style={{ fontSize: 13, color: c.textMuted, marginBottom: 20 }}>Set billing plan and terms for this tenant</div>
              <label style={labelS}>Plan</label>
              <select style={inputS} value={assignForm.plan_id} onChange={e => setAssignForm(p => ({ ...p, plan_id: e.target.value }))}>
                <option value="">— No Plan —</option>
                {plans.map(p => <option key={p.id} value={p.id}>{p.name} ({p.currency} {Number(p.price_monthly).toLocaleString()}/mo)</option>)}
              </select>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div>
                  <label style={labelS}>Billing Cycle</label>
                  <select style={inputS} value={assignForm.billing_cycle} onChange={e => setAssignForm(p => ({ ...p, billing_cycle: e.target.value }))}>
                    <option value="monthly">Monthly</option>
                    <option value="yearly">Yearly</option>
                    <option value="custom">Custom</option>
                  </select>
                </div>
                <div>
                  <label style={labelS}>Status</label>
                  <select style={inputS} value={assignForm.status} onChange={e => setAssignForm(p => ({ ...p, status: e.target.value }))}>
                    {['active','trial','past_due','cancelled','suspended'].map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
                <div>
                  <label style={labelS}>Custom Price (override)</label>
                  <input type="number" style={inputS} value={assignForm.custom_price} onChange={e => setAssignForm(p => ({ ...p, custom_price: e.target.value }))} placeholder="Leave blank to use plan price" />
                </div>
                <div>
                  <label style={labelS}>Currency</label>
                  <select style={inputS} value={assignForm.currency} onChange={e => setAssignForm(p => ({ ...p, currency: e.target.value }))}>
                    {['INR','USD','EUR','GBP'].map(cur => <option key={cur} value={cur}>{cur}</option>)}
                  </select>
                </div>
                <div>
                  <label style={labelS}>Next Billing Date</label>
                  <input type="date" style={inputS} value={assignForm.next_billing_date} onChange={e => setAssignForm(p => ({ ...p, next_billing_date: e.target.value }))} />
                </div>
                <div>
                  <label style={labelS}>Trial Ends At</label>
                  <input type="date" style={inputS} value={assignForm.trial_ends_at} onChange={e => setAssignForm(p => ({ ...p, trial_ends_at: e.target.value }))} />
                </div>
              </div>
              <label style={labelS}>Notes</label>
              <textarea style={{ ...inputS, height: 70, resize: 'vertical' }} value={assignForm.notes} onChange={e => setAssignForm(p => ({ ...p, notes: e.target.value }))} placeholder="Internal notes..." />
              <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
                <button style={{ background: 'none', border: `1px solid ${c.border}`, borderRadius: 8, padding: '10px 20px', fontSize: 13, cursor: 'pointer', color: c.text }} onClick={() => setAssignModal(null)}>Cancel</button>
                <button style={{ background: c.primary, color: '#fff', border: 'none', borderRadius: 8, padding: '10px 20px', fontSize: 13, fontWeight: 600, cursor: 'pointer', opacity: saving ? 0.7 : 1 }} onClick={saveAssign} disabled={saving}>{saving ? 'Saving...' : 'Save Assignment'}</button>
              </div>
            </div>
          </div>
        )}

        {/* Invoices Modal */}
        {invoicesModal && (
          <div style={{ position: 'fixed', inset: 0, background: '#0008', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }} onClick={() => setInvoicesModal(null)}>
            <div style={{ background: c.card, borderRadius: 14, padding: 28, width: 680, maxHeight: '90vh', overflowY: 'auto' }} onClick={e => e.stopPropagation()}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
                <div style={{ fontSize: 17, fontWeight: 700, color: c.text }}>📄 Invoices — {invoicesModal.company_name}</div>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button onClick={() => setShowNewInvoice(p => !p)} style={{ background: c.primary, color: '#fff', border: 'none', borderRadius: 7, padding: '7px 14px', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>+ New Invoice</button>
                  <button onClick={() => setInvoicesModal(null)} style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: c.textMuted }}>✕</button>
                </div>
              </div>
              {showNewInvoice && (
                <div style={{ background: c.bg, border: `1px solid ${c.border}`, borderRadius: 10, padding: 16, marginBottom: 16 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: c.text, marginBottom: 12 }}>New Invoice</div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10 }}>
                    <div><label style={labelS}>Amount *</label><input type="number" style={inputS} value={newInvoiceForm.amount} onChange={e => setNewInvoiceForm(p => ({ ...p, amount: e.target.value }))} placeholder="0.00" /></div>
                    <div><label style={labelS}>Period Start *</label><input type="date" style={inputS} value={newInvoiceForm.billing_period_start} onChange={e => setNewInvoiceForm(p => ({ ...p, billing_period_start: e.target.value }))} /></div>
                    <div><label style={labelS}>Period End *</label><input type="date" style={inputS} value={newInvoiceForm.billing_period_end} onChange={e => setNewInvoiceForm(p => ({ ...p, billing_period_end: e.target.value }))} /></div>
                  </div>
                  <div style={{ display: 'flex', gap: 10 }}>
                    <button onClick={createInvoice} style={{ background: c.primary, color: '#fff', border: 'none', borderRadius: 7, padding: '8px 18px', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>Create</button>
                    <button onClick={() => setShowNewInvoice(false)} style={{ background: 'none', border: `1px solid ${c.border}`, borderRadius: 7, padding: '8px 14px', fontSize: 12, cursor: 'pointer', color: c.textMuted }}>Cancel</button>
                  </div>
                </div>
              )}
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead><tr style={{ background: c.bg, borderBottom: `1px solid ${c.border}` }}>
                  {['Period', 'Plan', 'Amount', 'Status', 'Paid At', 'Actions'].map(h => (
                    <th key={h} style={{ textAlign: 'left', padding: '10px 12px', color: c.textMuted, fontWeight: 600, fontSize: 11 }}>{h}</th>
                  ))}
                </tr></thead>
                <tbody>
                  {invoices.map(inv => (
                    <tr key={inv.id} style={{ borderBottom: `1px solid ${c.border}` }}>
                      <td style={{ padding: '10px 12px', color: c.textMuted, fontSize: 12 }}>{new Date(inv.billing_period_start).toLocaleDateString()} – {new Date(inv.billing_period_end).toLocaleDateString()}</td>
                      <td style={{ padding: '10px 12px', color: c.text }}>{inv.plan_name || '—'}</td>
                      <td style={{ padding: '10px 12px', fontWeight: 600, color: c.text }}>{inv.currency} {Number(inv.amount).toLocaleString()}</td>
                      <td style={{ padding: '10px 12px' }}>
                        <span style={{ background: (invoiceStatusColors[inv.status] || {}).bg || '#f3f4f6', color: (invoiceStatusColors[inv.status] || {}).color || '#9ca3af', borderRadius: 20, padding: '2px 8px', fontSize: 11, fontWeight: 600 }}>{inv.status}</span>
                      </td>
                      <td style={{ padding: '10px 12px', color: c.textMuted, fontSize: 12 }}>{inv.paid_at ? new Date(inv.paid_at).toLocaleDateString() : '—'}</td>
                      <td style={{ padding: '10px 12px' }}>
                        {inv.status === 'pending' && (
                          <div style={{ display: 'flex', gap: 4 }}>
                            <button onClick={() => markInvoice(inv.id, 'paid')} style={{ background: c.successLight, color: c.success, border: 'none', borderRadius: 5, padding: '3px 8px', fontSize: 11, cursor: 'pointer' }}>Mark Paid</button>
                            <button onClick={() => markInvoice(inv.id, 'waived')} style={{ background: '#ede9fe', color: '#7c3aed', border: 'none', borderRadius: 5, padding: '3px 8px', fontSize: 11, cursor: 'pointer' }}>Waive</button>
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                  {!invoices.length && <tr><td colSpan={6} style={{ padding: 20, textAlign: 'center', color: c.textMuted }}>No invoices yet</td></tr>}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    );
  };

  const SaasComposePanel = () => {
    const [form, setForm] = React.useState({ to: '', subject: '', body: '' });
    const [status, setStatus] = React.useState('');
    const send = async () => {
      if (!form.to || !form.subject || !form.body) { setStatus('Fill all fields'); return; }
      try {
        const res = await fetch(`${API}/api/saas-admin/email/send`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({ to_email: form.to, subject: form.subject, body: form.body })
        });
        const data = await res.json();
        setStatus(data.success ? 'Sent!' : (data.error || 'Failed'));
        if (data.success) setForm({ to: '', subject: '', body: '' });
      } catch { setStatus('Network error'); }
    };
    return (
      <div>
        <div style={{ fontSize: 22, fontWeight: 700, color: c.text, marginBottom: 20 }}>Compose Email</div>
        <div style={{ background: c.card, borderRadius: 8, padding: 24, border: `1px solid ${c.border}`, maxWidth: 600 }}>
          {['to', 'subject'].map(f => (
            <div key={f} style={{ marginBottom: 14 }}>
              <label style={{ display: 'block', marginBottom: 5, fontWeight: 500, fontSize: 13, textTransform: 'capitalize', color: c.textMuted }}>{f}</label>
              <input value={form[f]} onChange={e => setForm(p => ({ ...p, [f]: e.target.value }))}
                style={{ width: '100%', padding: '8px 12px', border: `1px solid ${c.border}`, borderRadius: 6, fontSize: 13, boxSizing: 'border-box', outline: 'none', background: c.bg, color: c.text }} />
            </div>
          ))}
          <div style={{ marginBottom: 14 }}>
            <label style={{ display: 'block', marginBottom: 5, fontWeight: 500, fontSize: 13, color: c.textMuted }}>Message</label>
            <textarea value={form.body} onChange={e => setForm(p => ({ ...p, body: e.target.value }))}
              rows={7} style={{ width: '100%', padding: '8px 12px', border: `1px solid ${c.border}`, borderRadius: 6, fontSize: 13, resize: 'vertical', boxSizing: 'border-box', outline: 'none', background: c.bg, color: c.text }} />
          </div>
          {status && <p style={{ color: status === 'Sent!' ? c.success : c.danger, marginBottom: 12, fontSize: 13 }}>{status}</p>}
          <button onClick={send}
            style={{ background: c.primary, color: '#fff', border: 'none', padding: '10px 24px', borderRadius: 6, cursor: 'pointer', fontSize: 14, fontWeight: 600 }}>
            Send Email
          </button>
        </div>
      </div>
    );
  };

  const renderSection = () => {
    switch (section) {
      case 'dashboard': return <Dashboard />;
      case 'tenants': return <TenantsView />;
      case 'analytics': return <AnalyticsView />;
      case 'permissions': return <PermissionsView />;
      case 'api-keys': return <ApiKeysView />;
      case 'developer': return <DeveloperView />;
      case 'branding': return <BrandingView />;
      case 'compose': return <SaasComposePanel />;
      case 'security': return <SecurityView />;
      case 'billing': return <BillingView />;
      default: return <Dashboard />;
    }
  };

  return (
    <div style={{ display: 'flex', minHeight: '100vh', background: c.bg }}>
      <Sidebar />
      <div style={{ marginLeft: 220, flex: 1, padding: 28 }}>
        {renderSection()}
      </div>

      {dnsModal && (
        <div style={{ position: 'fixed', inset: 0, background: '#0008', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: c.card, borderRadius: 14, padding: 28, width: 520, maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 20px 60px #0003' }}>
            <div style={{ fontSize: 17, fontWeight: 700, color: c.text, marginBottom: 4 }}>✅ Tenant Created!</div>
            <div style={{ fontSize: 13, color: c.textMuted, marginBottom: 4 }}>Domain: <code style={{ fontFamily: 'monospace', background: c.bg, padding: '2px 6px', borderRadius: 4 }}>{dnsModal.domain}</code></div>
            {dnsModal.password && <div style={{ fontSize: 13, color: c.textMuted, marginBottom: 16 }}>Admin password: <code style={{ fontFamily: 'monospace', background: c.bg, padding: '2px 6px', borderRadius: 4 }}>{dnsModal.password}</code></div>}
            <div style={{ fontSize: 13, fontWeight: 600, color: c.text, marginBottom: 12 }}>Add these DNS records at your domain provider:</div>
            {[
              { type: 'MX',    name: dnsModal.domain,              value: 'mail.ssgzone.in',                                    note: 'Priority: 10' },
              { type: 'TXT',   name: dnsModal.domain,              value: 'v=spf1 include:ssgzone.in ~all',                     note: 'SPF' },
              { type: 'TXT',   name: `_dmarc.${dnsModal.domain}`, value: 'v=DMARC1; p=none; rua=mailto:dmarc@ssgzone.in',      note: 'DMARC' },
              { type: 'CNAME', name: `mail.${dnsModal.domain}`,   value: 'mail.ssgzone.in',                                    note: 'Webmail' },
            ].map((r, i) => (
              <div key={i} style={{ background: c.bg, border: `1px solid ${c.border}`, borderRadius: 8, padding: '10px 14px', marginBottom: 8, display: 'flex', alignItems: 'center', gap: 10 }}>
                <span style={{ background: c.primaryLight, color: c.primary, borderRadius: 4, padding: '2px 7px', fontSize: 11, fontWeight: 700, minWidth: 44, textAlign: 'center' }}>{r.type}</span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 11, color: c.textMuted, marginBottom: 2 }}>{r.name} {r.note && <span style={{ color: c.warning }}>({r.note})</span>}</div>
                  <div style={{ fontFamily: 'monospace', fontSize: 12, color: c.text, wordBreak: 'break-all' }}>{r.value}</div>
                </div>
                <button onClick={() => navigator.clipboard.writeText(r.value)}
                  style={{ background: c.card, border: `1px solid ${c.border}`, borderRadius: 6, padding: '4px 10px', fontSize: 11, cursor: 'pointer', color: c.textMuted, flexShrink: 0 }}>📋</button>
              </div>
            ))}
            <div style={{ fontSize: 11, color: c.warning, marginBottom: 16 }}>⚠ DNS propagation may take 5–30 minutes.</div>
            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <button onClick={() => setDnsModal(null)}
                style={{ background: c.primary, color: '#fff', border: 'none', borderRadius: 8, padding: '10px 20px', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>Done</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
